import { FC, useState, useCallback, useRef, useEffect } from "react"
import { OBLOResult } from "../../../../types/pricing-engine"
import { useLazyFindOBLOsQuery } from "../../../../redux/queries/pricing-engine"
import { useDebouncedCallback } from "../../../hooks"
import { Search, X } from "lucide-react"
import { PartialLoader } from "../../skeletons"

const DEBOUNCE_DELAY = 400

interface Props {
  brandId: string
  onSelect: (obLO: OBLOResult) => void
  onClear: () => void
  selectedOBLO: OBLOResult | null
}

const OBLOAutocomplete: FC<Props> = ({
  brandId,
  onSelect,
  onClear,
  selectedOBLO
}) => {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<OBLOResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const [findOBLOs, { isFetching }] = useLazyFindOBLOsQuery()

  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([])
        setIsOpen(false)
        return
      }

      try {
        const data = await findOBLOs({
          brand_id: brandId,
          name: searchQuery
        }).unwrap()
        setResults(data || [])
        setIsOpen(true)
      } catch (error) {
        setResults([])
      }
    },
    [findOBLOs, brandId]
  )

  const debouncedSearch = useDebouncedCallback(performSearch, DEBOUNCE_DELAY)

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setQuery(value)
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  const handleSelect = useCallback(
    (obLO: OBLOResult) => {
      onSelect(obLO)
      setQuery("")
      setResults([])
      setIsOpen(false)
    },
    [onSelect]
  )

  const handleClear = useCallback(() => {
    setQuery("")
    setResults([])
    setIsOpen(false)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  if (selectedOBLO) {
    return (
      <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-md p-3">
        <div className="flex-1">
          <div className="font-medium text-teal-800">
            {selectedOBLO.firstName} {selectedOBLO.lastName}
          </div>
          <div className="text-sm text-teal-600">{selectedOBLO.email}</div>
          <div className="text-xs text-teal-500">
            {selectedOBLO.biz_channel?.name}
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-teal-600 hover:text-teal-800 p-1"
        >
          <X size={16} />
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block font-medium mb-1">Search Pricing Engine LO</label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={16} className="text-gray-400" />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder="Search by name..."
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X size={16} className="text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
          {isFetching ? (
            <div className="p-4">
              <PartialLoader />
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-gray-500 text-center">
              No results found
            </div>
          ) : (
            <ul>
              {results.map((obLO, idx) => (
                <li
                  key={`${obLO.email}-${idx}`}
                  onClick={() => handleSelect(obLO)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <div className="font-medium">
                    {obLO.firstName} {obLO.lastName}
                  </div>
                  <div className="text-sm text-gray-500">{obLO.email}</div>
                  <div className="text-xs text-gray-400">
                    {obLO.biz_channel?.name}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default OBLOAutocomplete
